import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBlockedProductTaskTypeOptions } from 'hooks/api/options/blocked-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';

import { formSchema, type FormSchema } from './form-schema';

export type CreateTaskDrawerRef = {
  open: () => void;
  close: () => void;
};

type Props = {
  onSubmit: (data: FormSchema) => void;
  loading?: boolean;
};

export const CreateTaskDrawer
  = React.forwardRef<CreateTaskDrawerRef, Props>(({
                                                    onSubmit,
                                                    loading,
                                                  }, ref) => {
  const [open, setOpen] = React.useState(false);

  const taskOptions = useBlockedProductTaskTypeOptions();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
    form.reset();
  }, [form]);

  React.useImperativeHandle(ref, () => ({
    open: () => onOpenChange(true),
    close: () => onOpenChange(false),
  }), [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[800px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
              <span>Novo trabalho</span>
            </Drawer.Title>
          </Drawer.Header>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <FormField
                control={form.control}
                name={'task'}
                render={({ field }) => (
                  <ComboboxGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    content={taskOptions.singleSelectOptions}
                    label={'Tipo de trabalho'}
                    portal={false}
                  />
                )}
              />
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});
CreateTaskDrawer.displayName = 'CreateTaskDrawer';
